import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { TouristPlace, TouristPlacesState, TouristPlaceFormData } from '../../resources/types/touristPlacesTypes';
import { handleDefaultErrors } from '../../resources/functions';

const initialState: TouristPlacesState = {
  touristPlaces: [],
  loading: false,
  shouldUpdate: true,
};

export const touristPlacesSlice = createSlice({
  name: 'touristPlaces',
  initialState,
  reducers: {
    setTouristPlaces: (state, action: PayloadAction<TouristPlace[]>) => {
      state.touristPlaces = action.payload;
      state.shouldUpdate = false;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setShouldUpdate: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdate = action.payload;
    },
  },
});

const { setTouristPlaces, setLoading, setShouldUpdate } = touristPlacesSlice.actions;

export const getTouristPlacesAsync = (forceUpdate?: boolean): AppThunk => async (dispatch, getState) => {
  if (!getState().touristPlaces.shouldUpdate && !forceUpdate) return;
  try {
    dispatch(setLoading(true));
    const response = await axios.get('/tourist_places/admin');
    dispatch(setTouristPlaces(response.data.data || []));
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const createTouristPlaceAsync = (
  data: TouristPlaceFormData,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.post('/tourist_places', data);
    dispatch(setShouldUpdate(true));
    await dispatch(getTouristPlacesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const updateTouristPlaceAsync = (
  id: string,
  data: Partial<TouristPlaceFormData>,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.put(`/tourist_places/${id}`, data);
    dispatch(setShouldUpdate(true));
    await dispatch(getTouristPlacesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const deleteTouristPlaceAsync = (
  id: string,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.delete(`/tourist_places/${id}`);
    dispatch(setShouldUpdate(true));
    await dispatch(getTouristPlacesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const uploadTouristPlacePhotoAsync = async (file: File): Promise<string | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post('/files/persistent', formData);
    return response.data.path;
  } catch (error) {
    handleDefaultErrors(error);
    return null;
  }
};

export const selectTouristPlaces = (state: RootState) => state.touristPlaces;

export default touristPlacesSlice.reducer;
